<script lang="ts">
	import { HighlightedText, Icon } from '@repo/commons/components'

	interface Props {
		title: string
		expanded?: boolean
		collapsible?: boolean
		disabled?: boolean
		searchText?: string
		headerActions?: import('svelte').Snippet
		children?: import('svelte').Snippet
		onHighlightMatch?: (match: string, isExactMatch: boolean) => void
		onToggle?: (expanded: boolean) => void
	}

	let {
		title,
		expanded = $bindable(false),
		collapsible = true,
		disabled = false,
		searchText = '',
		headerActions,
		children,
		onToggle,
		onHighlightMatch
	}: Props = $props()

	const toggle = () => {
		if (disabled) return
		expanded = !expanded
		onToggle?.(expanded)
	}

	const handleKeydown = (event: KeyboardEvent) => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault()
			toggle()
		}
	}
</script>

<div class="accordion-item" class:expanded class:disabled>
	{#if collapsible}
		<div class="accordion-header-row">
			<button
				class="accordion-header"
				type="button"
				{disabled}
				aria-expanded={expanded}
				onclick={toggle}
				onkeydown={handleKeydown}
			>
				<span class="accordion-title">
					<HighlightedText text={title} pattern={searchText} {onHighlightMatch} />
				</span>
				<div class="accordion-chevron-wrapper" class:rotated={expanded}>
					<Icon name="chevronDown" class="accordion-chevron" />
				</div>
			</button>
			{@render headerActions?.()}
		</div>
	{:else if title || headerActions}
		<div class="accordion-header-row">
			{#if title}
				<span class="accordion-title accordion-static-title">
					<HighlightedText text={title} pattern={searchText} {onHighlightMatch} />
				</span>
			{/if}
			{@render headerActions?.()}
		</div>
	{/if}

	<div class="accordion-content" class:expanded={expanded || !collapsible}>
		<div class="accordion-content-inner">
			{@render children?.()}
		</div>
	</div>
</div>

<style>
	.accordion-item {
		@apply border border-[#002C4D80] rounded-[1.25rem] overflow-hidden bg-white transition-shadow duration-200;
	}

	/*.accordion-item:not(:last-child) {*/
	/*	@apply mb-2;*/
	/*}*/

	.accordion-item.disabled {
		@apply opacity-60 pointer-events-none;
	}

	.accordion-header {
		@apply w-full p-4 bg-transparent border-none flex items-center justify-between cursor-pointer text-base font-medium text-left transition-colors duration-200;
	}

	.accordion-header-row {
		@apply flex items-center;
	}

	.accordion-header:focus-visible {
		@apply outline-2 outline-blue-500 -outline-offset-2;
	}

	.accordion-title {
		@apply flex-1 text-[#001d33]/70 font-normal;
	}

	.accordion-static-title {
		@apply p-4;
	}

	.accordion-chevron-wrapper {
		@apply flex items-center justify-center w-6 h-6 text-gray-500 transition-transform duration-200;
	}

	.accordion-chevron-wrapper.rotated {
		@apply rotate-180;
	}

	:global(.accordion-chevron) {
		@apply text-[#002C4D]/50;
	}

	.accordion-content {
		display: grid;
		grid-template-rows: 0fr;
		transition: grid-template-rows 300ms ease-in-out;
	}

	.accordion-content.expanded {
		grid-template-rows: 1fr;
	}

	.accordion-content-inner {
		overflow: hidden;
	}

	:global(.accordion-content-wrapper) {
		@apply px-4 pb-5 pt-3;
	}
</style>
